import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../core/api/api.config';
import { FrontendCoupon, FrontendDiscountPage } from '../../core/api/api.models';
import { AssetUrlPipe } from '../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../core/api/trusted-cms-html.pipe';
import { CouponCard } from '../../shared/coupon-card/coupon-card';
import { SectionHeading } from '../../shared/section-heading/section-heading';
import { toCouponCardView } from '../../shared/coupon-card/coupon-card.mapper';
import { CouponCardView } from '../../shared/coupon-card/coupon-card.models';
import { SeoService } from '../../core/seo/seo.service';
import { SsrResponseService } from '../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../core/seo/breadcrumb-trail';
import { faqPageSchema, webPageSchema } from '../../core/seo/structured-data';
import { Breadcrumb } from '../../shared/breadcrumb/breadcrumb';
import { AffiliateLinkService } from '../../core/affiliate/affiliate-link.service';

/** One admin-authored content block, rendered only when it has content. */
interface ContentSection {
  heading: string;
  subtitle: string;
  html: string;
}

/** A key/value row in the sidebar summary. Only rows with a real value are built. */
interface DetailRow {
  label: string;
  value: string;
  accent?: boolean;
}

/**
 * `/{store}-student-discount` and its teacher/military siblings.
 *
 * The route is matched by pattern (see `discountPageMatcher` in app.routes.ts) because the slug is
 * generated from the store, so it cannot be enumerated as a static path — and it has to be tried
 * before the `:storeSlug` catch-all, which would otherwise swallow it.
 */
@Component({
  selector: 'app-discount-page',
  imports: [Breadcrumb, RouterLink, AssetUrlPipe, TrustedCmsHtmlPipe, CouponCard, SectionHeading],
  templateUrl: './discount-page.html',
  styleUrl: './discount-page.scss',
})
export class DiscountPage {
  private readonly route = inject(ActivatedRoute);
  private readonly affiliate = inject(AffiliateLinkService);
  private readonly api = inject(SavingArenaApiService);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly page = signal<FrontendDiscountPage | null>(null);

  /**
   * Home / Stores / <store> / <discount type>.
   *
   * The visible trail stopped at "Stores" and then jumped straight to "<store> <type>" as one
   * crumb, while the schema went Home / <store> / <type> with no Stores step. This keeps the
   * store as its own linked crumb — it is a real page above this one — and the discount type as
   * the page you are on.
   */
  protected readonly crumbs = computed(() => {
    const page = this.page();
    if (!page) return breadcrumbTrail({ label: 'Stores', path: '/storelist' });
    return breadcrumbTrail(
      { label: 'Stores', path: '/storelist' },
      { label: page.store.name, path: `/${page.store.slug}` },
      { label: page.discountTypeLabel },
    );
  });
  protected readonly isLoading = signal(true);
  /** Only claim "not found" once the request has actually resolved. */
  protected readonly notFound = computed(() => !this.isLoading() && this.page() === null);

  /** Index of the open accordion item; one at a time, first open by default. */
  protected readonly expandedFaq = signal(0);

  /** The store's own coupon page — built from the live store slug, never typed by an admin. */
  protected readonly storePath = computed(() => {
    const slug = this.page()?.store?.slug?.trim();
    return slug ? `/${slug}` : '';
  });

  protected readonly coupons = computed(() => this.page()?.coupons ?? []);
  protected readonly deals = computed(() => this.page()?.deals ?? []);
  protected readonly relatedPages = computed(() => this.page()?.relatedPages ?? []);
  /** The same store's other discount pages — every page of a store links to its siblings. */
  protected readonly storePages = computed(() => this.page()?.storePages ?? []);

  /**
   * The offers as the site's shared coupon card sees them.
   *
   * Built with the same mapper the store page uses, so a coupon renders identically on both pages.
   */
  protected readonly couponCards = computed(() => this.toCards(this.coupons()));
  protected readonly dealCards = computed(() => this.toCards(this.deals()));

  protected readonly ratingValue = computed(() => Number(this.page()?.store?.['rating'] ?? 0));
  protected readonly ratingCount = computed(() => Number(this.page()?.store?.['ratingCount'] ?? 0));
  protected readonly hasRating = computed(() => this.ratingCount() > 0);

  /** Whole-number star row; the numeric value is printed separately. */
  protected readonly ratingStars = computed(() => {
    const rounded = Math.round(this.ratingValue());
    return [1, 2, 3, 4, 5].map((position) => position <= rounded);
  });

  /**
   * "How to claim" is admin-authored rich text, but the design presents it as a numbered stepper.
   * Splitting an authored list into steps here would mean parsing HTML, so the stepper look is
   * applied in CSS to whatever ordered list the admin wrote — see `.sav-dp-steps` in the styles.
   */
  protected readonly howToClaim = computed(() => this.page()?.howToClaim?.trim() ?? '');

  /** Content blocks in reading order, with the empty ones dropped. */
  protected readonly sections = computed<ContentSection[]>(() => {
    const page = this.page();
    if (!page) return [];

    const store = page.store.name;
    const type = page.discountTypeLabel.toLowerCase();

    return [
      {
        heading: `About the ${store} ${type}`,
        subtitle: `What ${store} sells, and how its ${type} works.`,
        html: page.mainContent,
      },
      {
        heading: 'Discount details',
        subtitle: `The saving itself — how much, and on what.`,
        html: page.discountDetails,
      },
      {
        heading: 'Who is eligible',
        subtitle: 'Who can claim it, and what counts as proof.',
        html: page.eligibility,
      },
      {
        heading: 'Verification requirements',
        subtitle: 'How your status is checked, and how long it lasts.',
        html: page.verificationRequirements,
      },
      {
        heading: 'Terms and conditions',
        subtitle: `The rules ${store} applies to this discount.`,
        html: page.termsAndConditions,
      },
    ].filter((section) => section.html && section.html.trim().length > 0);
  });

  /**
   * The sidebar summary.
   *
   * Every row is derived from data that actually exists — there are no placeholder figures here.
   * A row whose source is empty is dropped rather than rendered as "N/A".
   */
  protected readonly detailRows = computed<DetailRow[]>(() => {
    const page = this.page();
    if (!page) return [];

    const rows: DetailRow[] = [
      { label: 'Discount type', value: page.discountTypeLabel, accent: true },
      { label: 'Store', value: page.store.name },
    ];

    const domain = String(page.store['domain'] ?? '').trim();
    if (domain) rows.push({ label: 'Where it works', value: domain });

    const codeCount = this.coupons().length;
    const dealCount = this.deals().length;
    if (codeCount + dealCount > 0) {
      rows.push({
        label: 'Live offers',
        value: [
          codeCount ? `${codeCount} code${codeCount === 1 ? '' : 's'}` : '',
          dealCount ? `${dealCount} deal${dealCount === 1 ? '' : 's'}` : '',
        ]
          .filter(Boolean)
          .join(' · '),
      });
    }

    if (page.verificationRequirements.trim())
      rows.push({ label: 'Verification', value: 'Required' });

    const checked = this.formatDate(page.updatedAt);
    if (checked) rows.push({ label: 'Last checked', value: checked });

    return rows;
  });

  /** "Verified" is only claimed when an admin actually flagged an offer verified. */
  protected readonly hasVerifiedOffer = computed(() =>
    [...this.coupons(), ...this.deals()].some((offer) => offer.isVerified),
  );

  protected readonly offerCountLabel = computed(() => {
    const total = this.coupons().length + this.deals().length;
    return total > 0 ? `${total} live offer${total === 1 ? '' : 's'}` : '';
  });

  /**
   * Other stores running this same discount, the ones sharing this store's category first.
   *
   * Empty until more stores have a published page of this type — the list is built from real pages
   * only, because every tile here opens one.
   */
  protected readonly relatedStores = computed(() => this.page()?.relatedPages ?? []);

  protected readonly sameCategoryStoreCount = computed(
    () => this.relatedStores().filter((related) => related.sameCategory).length,
  );

  /** "Other Automotive stores offering a student discount" when we know the category. */
  protected readonly relatedStoresHeading = computed(() => {
    const page = this.page();
    if (!page) return '';

    const type = page.discountTypeLabel.toLowerCase();
    const category = (page.storeCategoryNames ?? [])[0];
    return category && this.sameCategoryStoreCount() > 0
      ? `Other ${category} stores with a ${type}`
      : `More stores with a ${type}`;
  });

  protected readonly relatedStoresSubtitle = computed(() => {
    const total = this.relatedStores().length;
    const type = (this.page()?.discountTypeLabel ?? '').toLowerCase();
    return `${total} store${total === 1 ? '' : 's'} we track with a published ${type} page.`;
  });

  /** True when the page has neither a code nor a deal to show right now. */
  protected readonly hasNoOffers = computed(
    () => this.coupons().length + this.deals().length === 0,
  );

  protected readonly lastCheckedLabel = computed(() =>
    this.formatDate(this.page()?.updatedAt ?? null),
  );

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => (params.get('discountSlug') ?? '').trim()),
        switchMap((slug) => {
          this.isLoading.set(true);
          this.page.set(null);
          if (!slug) return of(null);

          return this.api.getDiscountPage(slug).pipe(
            map((response) => (response.success ? response.data : null)),
            catchError((error: unknown) => {
              this.ssrResponse.noteFetchFailure(error);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        this.page.set(page);
        this.isLoading.set(false);
        this.expandedFaq.set(0);
        this.applySeo();
      });
  }

  protected toggleFaq(index: number): void {
    this.expandedFaq.update((current) => (current === index ? -1 : index));
  }

  /**
   * Show Code / Get Deal — the same two destinations a coupon opens everywhere else on the site, so
   * an offer behaves identically wherever it is clicked:
   *  - a NEW tab gets the store's offer URL `/<store>/coupon/<id>`, which the store page turns into
   *    the popup, so the code is on screen and the page can be shared;
   *  - THIS tab follows the `/go/:store/:id` redirect to the merchant.
   *
   * Order matters. The new tab is opened first, while the browser still counts this as a user
   * gesture — set `location` first and the pending `window.open` is discarded as the page unloads.
   *
   * The redirect increments the coupon's Hits counter server-side, so nothing is recorded here.
   */
  protected openOffer(card: CouponCardView): void {
    if (!this.isBrowser) return;

    const store = this.page()?.store;
    const slug = store?.slug ?? '';
    if (!slug || !card.id) return;

    this.affiliate.openCoupon({
      storeSlug: slug,
      couponId: card.id,
      store: store?.name || slug,
      kind: 'discount-page',
    });
  }

  private toCards(offers: FrontendCoupon[]): CouponCardView[] {
    const store = this.page()?.store;
    const name = store?.name ?? '';

    return offers.map((offer, index) =>
      toCouponCardView(offer, {
        assetBaseUrl: this.apiConfig.assetBaseUrl,
        fallbackTitle: `${name} offer`,
        fallbackStoreName: name,
        index,
      }),
    );
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private applySeo(): void {
    const page = this.page();

    if (!page) {
      // A slug with no published page behind it is a real 404, not a 200 empty shell — a draft must
      // never return a soft 200 that Google can index.
      if (this.isLoading()) return;
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Page not found | SavingArena',
        description: 'This discount page is not available.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const path = `/${page.slug}`;
    const url = this.seo.absolute(path);
    const title =
      page.metaTitle ||
      `${page.title || `${page.store.name} ${page.discountTypeLabel}`} | SavingArena`;
    const description =
      page.metaDescription ||
      `${page.discountTypeLabel} information for ${page.store.name}: eligibility, verification and the live offers listed on SavingArena.`;

    this.seo.apply({
      title,
      description,
      canonicalPath: path,
      image: page.featuredImagePath || page.store.logoImagePath || undefined,
    });

    const schema: Record<string, unknown>[] = [
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ];

    // faqPageSchema returns null when no FAQ has both a question and an answer — emitting an empty
    // FAQPage would be a structured-data error, so only push a real one.
    const faqSchema = faqPageSchema(
      page.faqs.map((faq) => ({ question: faq.question, answer: faq.answer })),
    );
    if (faqSchema) schema.push(faqSchema);

    this.seo.setJsonLd(schema);
  }
}
